import AddIcon from '@mui/icons-material/Add';
import { Box, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import type { ReactElement } from 'react';

import { SectionTypeIcon } from './SectionTypeIcon';
import type {
  SectionType,
  SectionTypeGroup,
  SectionTypeTooltip,
} from './hooks/useGetGroupedSectionTypes';

interface SectionTypePickerProps {
  groups: SectionTypeGroup[];
  isLoading: boolean;
  isError: boolean;
  onAdd: (sectionType: SectionType) => void;
}

export function SectionTypePicker({
  groups,
  isLoading,
  isError,
  onAdd,
}: SectionTypePickerProps): ReactElement {
  if (isLoading) {
    return <CircularProgress size={20} />;
  }

  if (isError) {
    return (
      <Typography variant="body2" className="vtwo:text-red-600">
        Failed to load section types
      </Typography>
    );
  }

  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-6">
      {groups.map((group, index) => (
        <Box
          // Position disambiguates uncategorised groups, which carry no id of their own.
          key={group.category?.id ?? `uncategorised-${index}`}
          className="vtwo:flex vtwo:flex-col vtwo:gap-2"
        >
          {group.category && (
            <Typography variant="subtitle2" className="vtwo:font-bold">
              {group.category.displayName}
            </Typography>
          )}
          {group.sectionTypes.map((sectionType) => (
            <SectionTypeCard
              key={sectionType.id}
              sectionType={sectionType}
              onAdd={() => onAdd(sectionType)}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

interface SectionTypeCardProps {
  sectionType: SectionType;
  onAdd: () => void;
}

// MUI's own emotion CSS is unlayered while Tailwind lives in `@layer app-utilities`, and unlayered
// declarations outrank any layer. Utilities that fight a property MUI already sets therefore need
// the `!` modifier; the rest apply normally.
const ADD_BUTTON_CLASSES =
  'vtwo:p-0! vtwo:rounded-lg! vtwo:bg-featured! vtwo:text-white! vtwo:hover:bg-featured-hover!';

const TOOLTIP_SURFACE_CLASSES =
  'vtwo:max-w-[360px]! vtwo:rounded-xl! vtwo:bg-white! vtwo:p-2.5! vtwo:text-typography-primary! vtwo:shadow-[0_8px_24px_rgba(0,0,0,0.16)]';

function SectionTypeCard({ sectionType, onAdd }: SectionTypeCardProps): ReactElement {
  const card = (
    <Box className="vtwo:flex vtwo:items-center vtwo:gap-3 vtwo:rounded-xl vtwo:border vtwo:border-plus-border vtwo:bg-white vtwo:p-3">
      <SectionTypeIcon iconUrl={sectionType.iconUrl} />
      <Typography className="vtwo:flex-1 vtwo:font-bold vtwo:text-typography-primary">
        {sectionType.displayName}
      </Typography>
      <IconButton
        onClick={onAdd}
        aria-label={`Add ${sectionType.displayName}`}
        className={`vtwo:h-8 vtwo:w-8 vtwo:shrink-0 ${ADD_BUTTON_CLASSES}`}
      >
        <AddIcon className="vtwo:text-[18px]!" />
      </IconButton>
    </Box>
  );

  if (!sectionType.tooltip) return card;

  return (
    <Tooltip
      title={<SectionTypeTooltipContent tooltip={sectionType.tooltip} />}
      describeChild
      placement="right"
      slotProps={{ tooltip: { className: TOOLTIP_SURFACE_CLASSES } }}
    >
      {card}
    </Tooltip>
  );
}

// The API pairs the explanatory copy with an example screenshot of the rendered section. The asset
// already carries its own frame, so no border is added here.
function SectionTypeTooltipContent({ tooltip }: { tooltip: SectionTypeTooltip }): ReactElement {
  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-2">
      {tooltip.tooltipImageUrl && (
        <img src={tooltip.tooltipImageUrl} alt="" aria-hidden="true" className="vtwo:w-full" />
      )}
      <Typography variant="body2" className="vtwo:text-typography-primary">
        {tooltip.tooltipText}
      </Typography>
    </Box>
  );
}
